/**
 * Produce an ArcFace-aligned 112x112 crop from a live video frame.
 *
 * Two defects in `svc-biometric-embedding` are corrected here, on our side, without touching a
 * service that the customer identity-verification journey also depends on:
 *
 * 1. **No landmark alignment.** It crops by bounding box and discards the five keypoints. We
 *    warp the face ourselves so the eyes, nose and mouth land where the model expects. Because
 *    we upload a tight 112x112 tile that the face already fills, the service's own crop is
 *    close to a no-op and our alignment survives.
 *
 * 2. **Swapped colour channels.** `sharp` hands it RGB, but it writes `b, g, r` into the tensor
 *    while InsightFace's `get_feat` uses `swapRB=True`, i.e. the model wants RGB. We therefore
 *    exchange red and blue before upload: the service's swap then puts them back, and the model
 *    finally receives true RGB. Cancelling a bug from outside is not elegant, but the
 *    alternative — editing the shared service — changes every embedding it has ever produced
 *    and would invalidate stored customer templates.
 */

import {
  ARCFACE_TILE_SIZE,
  alignFivePoints,
  applyTransform,
  type AlignmentQuality,
  type FaceLandmarks,
  type Point2,
} from '../core/index.js';

/**
 * MediaPipe indices for the five ArcFace keypoints.
 *
 * Eye and mouth landmarks are given as unordered pairs and resolved by measured image-x, for
 * the same reason as in the pose estimator: numbering schemes disagree about whose "left" is
 * meant, and a mirrored capture inverts it again. Getting this backwards would mirror every
 * aligned tile and make no two embeddings comparable.
 */
const FIVE_POINT_INDICES = {
  eyeOuterPair: [33, 263] as const,
  eyeInnerPair: [133, 362] as const,
  noseTip: 1,
  mouthCornerPair: [61, 291] as const,
};

export interface AlignedFace {
  readonly blob: Blob;
  readonly quality: AlignmentQuality;
}

function midpoint(a: Point2, b: Point2): Point2 {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

function orderByX(a: Point2, b: Point2): [Point2, Point2] {
  return a[0] <= b[0] ? [a, b] : [b, a];
}

/**
 * Extract the five ArcFace keypoints in video-pixel space.
 *
 * `streamIsMirrored` un-does the horizontal flip applied when landmarks were detected, because
 * the warp has to be expressed in the coordinate space of the actual video pixels we are about
 * to draw.
 */
export function extractFivePoints(
  landmarks: FaceLandmarks,
  streamIsMirrored: boolean,
): Point2[] | null {
  const { points, imageWidth, imageHeight } = landmarks;

  const at = (index: number): Point2 | null => {
    const point = points[index];
    if (point === undefined) return null;
    const x = streamIsMirrored ? 1 - point[0] : point[0];
    return [x * imageWidth, point[1] * imageHeight];
  };

  const outerA = at(FIVE_POINT_INDICES.eyeOuterPair[0]);
  const outerB = at(FIVE_POINT_INDICES.eyeOuterPair[1]);
  const innerA = at(FIVE_POINT_INDICES.eyeInnerPair[0]);
  const innerB = at(FIVE_POINT_INDICES.eyeInnerPair[1]);
  const nose = at(FIVE_POINT_INDICES.noseTip);
  const mouthA = at(FIVE_POINT_INDICES.mouthCornerPair[0]);
  const mouthB = at(FIVE_POINT_INDICES.mouthCornerPair[1]);

  if (
    outerA === null ||
    outerB === null ||
    innerA === null ||
    innerB === null ||
    nose === null ||
    mouthA === null ||
    mouthB === null
  ) {
    return null;
  }

  // Eye centre is the midpoint of its outer and inner corner — steadier than either alone,
  // and closer to what the ArcFace template's eye position actually represents.
  const [outerLeft, outerRight] = orderByX(outerA, outerB);
  const [innerLeft, innerRight] = orderByX(innerA, innerB);
  const eyeLeft = midpoint(outerLeft, innerLeft);
  const eyeRight = midpoint(outerRight, innerRight);
  const [mouthLeft, mouthRight] = orderByX(mouthA, mouthB);

  return [eyeLeft, eyeRight, nose, mouthLeft, mouthRight];
}

let tile: HTMLCanvasElement | null = null;

function tileContext(): CanvasRenderingContext2D | null {
  if (tile === null) {
    if (typeof document === 'undefined') return null;
    tile = document.createElement('canvas');
    tile.width = ARCFACE_TILE_SIZE;
    tile.height = ARCFACE_TILE_SIZE;
  }
  return tile.getContext('2d', { willReadFrequently: true });
}

/**
 * Warp the current video frame into an aligned 112x112 JPEG.
 *
 * Returns null when the landmarks cannot be aligned — a deliberately unusable result rather
 * than a distorted tile that would embed as somebody else.
 */
export async function alignFaceToJpeg(
  video: HTMLVideoElement,
  landmarks: FaceLandmarks,
  options: { streamIsMirrored?: boolean; swapRedBlue?: boolean; quality?: number } = {},
): Promise<AlignedFace | null> {
  const streamIsMirrored = options.streamIsMirrored ?? false;
  const swapRedBlue = options.swapRedBlue ?? true;

  if (video.videoWidth === 0) return null;

  const source = extractFivePoints(landmarks, streamIsMirrored);
  if (source === null) return null;

  const quality = alignFivePoints(source);
  if (quality === null || !quality.acceptable) return null;

  const context = tileContext();
  if (context === null) return null;

  const { a, b, tx, ty } = quality.transform;
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.clearRect(0, 0, ARCFACE_TILE_SIZE, ARCFACE_TILE_SIZE);
  // Grey rather than black where the warp reaches outside the frame, matching the letterbox
  // fill the service itself uses, so an edge case does not introduce a hard black border.
  context.fillStyle = 'rgb(127,127,127)';
  context.fillRect(0, 0, ARCFACE_TILE_SIZE, ARCFACE_TILE_SIZE);

  // Canvas maps (x,y) -> (a*x + c*y + e, b*x + d*y + f); a similarity transform is
  // (a, b, -b, a, tx, ty).
  context.setTransform(a, b, -b, a, tx, ty);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(video, 0, 0);
  context.setTransform(1, 0, 0, 1, 0, 0);

  if (swapRedBlue) {
    const image = context.getImageData(0, 0, ARCFACE_TILE_SIZE, ARCFACE_TILE_SIZE);
    const data = image.data;
    for (let i = 0; i < data.length; i += 4) {
      const red = data[i] as number;
      data[i] = data[i + 2] as number;
      data[i + 2] = red;
    }
    context.putImageData(image, 0, 0);
  }

  const blob = await new Promise<Blob | null>((resolve) => {
    (tile as HTMLCanvasElement).toBlob(
      (result) => resolve(result),
      'image/jpeg',
      options.quality ?? 0.95,
    );
  });
  if (blob === null) return null;

  return { blob, quality };
}

/** Where the five points land after alignment — for drawing a debug overlay. */
export function alignedKeypoints(quality: AlignmentQuality, source: readonly Point2[]): Point2[] {
  return source.map((point) => applyTransform(quality.transform, point));
}
